import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'sonner';
import { dealKeys } from '@/lib/query-keys';
import { dealService } from '@/services/deal.service';
import { useDealFilterStore } from '@/stores/dealFilterStore';

export function useDeals() {
  const queryClient = useQueryClient();
  const filters = useDealFilterStore();

  const dealsQuery = useQuery({
    queryKey: dealKeys.list(filters.current),
    queryFn: () => dealService.getAll(filters.current),
    placeholderData: keepPreviousData,
  });

  const createDeal = useMutation({
    mutationFn: (data: Parameters<typeof dealService.create>[0]) => dealService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dealKeys.lists() });
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      toast.success('Deal created successfully');
    },
    onError: () => toast.error('Failed to create deal'),
  });

  const updateDeal = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof dealService.update>[1] }) =>
      dealService.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: dealKeys.lists() });
      queryClient.invalidateQueries({ queryKey: dealKeys.detail(id) });
      toast.success('Deal updated successfully');
    },
    onError: () => toast.error('Failed to update deal'),
  });

  const deleteDeal = useMutation({
    mutationFn: (id: string) => dealService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      toast.success('Deal deleted successfully');
    },
    onError: () => toast.error('Failed to delete deal'),
  });

  const moveStage = useMutation({
    mutationFn: ({ id, stageId }: { id: string; stageId: string }) => dealService.moveStage(id, stageId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      toast.success('Deal moved successfully');
    },
    onError: () => toast.error('Failed to move deal'),
  });

  const markWon = useMutation({
    mutationFn: (id: string) => dealService.markWon(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      toast.success('Deal marked as won');
    },
    onError: () => toast.error('Failed to mark deal as won'),
  });

  const markLost = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => dealService.markLost(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: dealKeys.all });
      toast.success('Deal marked as lost');
    },
    onError: () => toast.error('Failed to mark deal as lost'),
  });

  return {
    deals: dealsQuery.data?.data ?? [],
    meta: dealsQuery.data?.meta,
    isLoading: dealsQuery.isLoading,
    filters,
    createDeal,
    updateDeal,
    deleteDeal,
    moveStage,
    markWon,
    markLost,
    exportCsv: () => dealService.exportCsv(filters.current),
  };
}

export function usePipelineDeals(pipelineId: string) {
  const queryClient = useQueryClient();

  const pipelineDealsQuery = useQuery({
    queryKey: dealKeys.pipeline(pipelineId),
    queryFn: () => dealService.getPipelineDeals(pipelineId),
    enabled: !!pipelineId,
  });

  const moveStage = useMutation({
    mutationFn: ({ id, stageId }: { id: string; stageId: string }) => dealService.moveStage(id, stageId),
    onMutate: async (_vars) => {
      await queryClient.cancelQueries({ queryKey: dealKeys.pipeline(pipelineId) });
      const previousData = queryClient.getQueryData(dealKeys.pipeline(pipelineId));
      // Optimistic update placeholder
      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(dealKeys.pipeline(pipelineId), context.previousData);
      }
      toast.error('Failed to move deal');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: dealKeys.pipeline(pipelineId) });
    },
  });

  return {
    stages: (pipelineDealsQuery.data?.data ?? []).map((stage) => ({
      ...stage,
      count: stage.deals?.length ?? 0,
      totalValue: stage.deals?.reduce((sum, d) => sum + Number(d.value || 0), 0) ?? 0,
    })),
    isLoading: pipelineDealsQuery.isLoading,
    moveStage,
  };
}

export function useDeal(id: string) {
  const queryClient = useQueryClient();

  const dealQuery = useQuery({ queryKey: dealKeys.detail(id), queryFn: () => dealService.getById(id), enabled: !!id });
  const activitiesQuery = useQuery({ queryKey: dealKeys.activities(id), queryFn: () => dealService.getActivities(id), enabled: !!id });
  const notesQuery = useQuery({ queryKey: dealKeys.notes(id), queryFn: () => dealService.getNotes(id), enabled: !!id });
  const tasksQuery = useQuery({ queryKey: dealKeys.tasks(id), queryFn: () => dealService.getTasks(id), enabled: !!id });

  const invalidate = () => { queryClient.invalidateQueries({ queryKey: dealKeys.detail(id) }); };

  return {
    deal: dealQuery.data?.data,
    isLoading: dealQuery.isLoading,
    activities: activitiesQuery.data?.data ?? [],
    notes: notesQuery.data?.data ?? [],
    tasks: tasksQuery.data?.data ?? [],
    invalidate,
  };
}
